'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import classnames from 'classnames';

import Box from '../../atoms/Box/Box';
import Dropdown from '../../atoms/Dropdown/Dropdown';
import Typography from '../../atoms/Typography/Typography';
import Header from './Header/Header';
import Navigation from './Navigation/Navigation';
import Footer from './Footer/Footer';
import Slider from './Slider/Slider';
import LanguageSelector from './LanguageSelector/LanguageSelector';
import LanguageToggle from './LanguageToggle/LanguageToggle';
import { DashboardIcon, SettingsIcon, KnowledgeCenterIcon } from './Icons';

import defaultSidebarData from './sidebar.json';
import rolesData from './roles.json';
import { AUTH0_ROLE_MAP } from './navigation';

import styles from './Sidebar.module.scss';

/**
 * Icon configuration for navigation items
 */
export type NavigationIcon = {
  category: string;
  name: string;
};

/**
 * Child navigation item (submenu item)
 */
export type NavigationChild = {
  id: string;
  label: string;
  path: string;
  icon?: NavigationIcon;
  /** If true, clicking this item opens the path in a new browser tab */
  openInNewTab?: boolean;
};

/**
 * Workspace/parent navigation item
 */
export type NavigationItem = {
  id: string;
  path: string;
  label: string;
  icon?: NavigationIcon;
  /** Child navigation items. Required for workspace items. */
  childrens: NavigationChild[];
};

/**
 * Labels for sidebar localization
 * Keys follow the pattern: sidebar.[workspace-id] or sidebar.[workspace-id].[child-id]
 */
export type SidebarLabels = {
  /** Dashboard button label */
  dashboard?: string;
  /** Settings button label */
  settings?: string;
  /** Sign out button label */
  signOut?: string;
  /** Workspace section title */
  workspace?: string;
  /** Settings appearance label */
  appearance?: string;
  /** Dark mode label */
  darkMode?: string;
  /** Light mode label */
  lightMode?: string;
  /** Region selector label */
  region?: string;
  /** Language toggle label */
  language?: string;
  /** Language toggle description */
  languageDescription?: string;
  /** Dynamic workspace/menu labels keyed by id (e.g., 'prospect-desk', 'prospect-desk.prospects') */
  [key: string]: string | undefined;
};

interface SidebarProps {
  theme?: 'light' | 'dark';
  userName?: string;
  userEmail?: string;
  showRoleDropdown?: boolean;
  currentPath?: string;
  /** Current locale/region (e.g., 'de-de', 'en-us'). Controls the region dropdown. */
  locale?: string;
  /** Controlled collapsed state */
  isCollapsed?: boolean;
  onCollapseChange?: (isCollapsed: boolean) => void;
  onToggleTheme?: () => void;
  onSignOut?: () => void;
  onProfileClick?: () => void;
  onLocaleChange?: (locale: string) => void;
  /** Override the locale groups shown in the region selector. */
  localeOptions?: { groupLabel: string; data: { value: string; label: string }[] }[];
  /** Currently selected label language (null = native locale language) */
  selectedLanguage?: string | null;
  /** Callback when label language changes */
  onLanguageChange?: (languageCode: string | null) => void;
  /** i18n labels for sidebar localization */
  labels?: SidebarLabels;
  /** Navigation items to display in sidebar. If not provided, uses default sidebar.json */
  navigationItems?: NavigationItem[];
  /** Callback for client-side navigation. If not provided, falls back to window.location.href */
  onNavigate?: (path: string) => void;
  /** Route prefixes owned by the current app. Paths not matching any prefix trigger a full page load instead of client-side navigation. */
  ownRoutePrefixes?: string[];
  /** Optional link rendered at the top of the Settings drawer (e.g. Knowledge Center) */
  knowledgeCenterLink?: { href: string; label: string };
  /** Auth0 roles for the current user. Used to auto-select the correct initial role. */
  userRoles?: string[];
}

const Sidebar: React.FC<SidebarProps> = ({
  theme = 'dark',
  userName = 'Johan Cruyff',
  userEmail = 'johan@lucidmotors.com',
  showRoleDropdown = false,
  currentPath: currentPathProp,
  locale: localeProp,
  isCollapsed: isCollapsedProp,
  onCollapseChange,
  onToggleTheme,
  onSignOut,
  onProfileClick,
  onLocaleChange,
  localeOptions,
  selectedLanguage: selectedLanguageProp = null,
  onLanguageChange,
  labels,
  navigationItems,
  onNavigate,
  ownRoutePrefixes,
  knowledgeCenterLink,
  userRoles,
}) => {
  // Use provided navigation items or fall back to default
  const sidebarData = useMemo(() => navigationItems ?? defaultSidebarData.data, [navigationItems]);

  const [internalCollapsed, setInternalCollapsed] = useState(false);
  // Use ref to track manual override so it doesn't trigger effect re-runs
  const hasManualOverrideRef = React.useRef(false);
  const COLLAPSE_BREAKPOINT = 1280;

  // Use controlled state if provided, otherwise use internal state
  const isControlled = isCollapsedProp !== undefined;
  const isCollapsed = isControlled ? isCollapsedProp : internalCollapsed;
  const [isHovered, setIsHovered] = useState(false);
  const [sliderType, setSliderType] = useState<'workspace' | 'settings' | null>(null);
  const [isSliderClosing, setIsSliderClosing] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>(rolesData.data[0].id);
  const hasManualRoleSelectRef = React.useRef(false);
  const [isDashboardSelected, setIsDashboardSelected] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(localeProp || 'de-de');

  // Track if initial mount has occurred
  const isInitialMountRef = React.useRef(true);

  // Sync selectedRole with the user's Auth0 roles when they load (skip if manually overridden)
  useEffect(() => {
    if (hasManualRoleSelectRef.current || !userRoles || userRoles.length === 0) return;
    // Translate Auth0 role names → sidebar IDs using the centralized map
    const sidebarId = userRoles.map((r) => AUTH0_ROLE_MAP[r]).find(Boolean);
    if (sidebarId) {
      setSelectedRole(sidebarId);
    }
  }, [userRoles]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(`(max-width: ${COLLAPSE_BREAKPOINT - 1}px)`);

    const applyCollapsedState = (shouldCollapse: boolean) => {
      if (isControlled) {
        if (isCollapsedProp !== shouldCollapse) {
          onCollapseChange?.(shouldCollapse);
        }
        return;
      }

      setInternalCollapsed(shouldCollapse);
    };

    const handleViewportChange = (event: MediaQueryListEvent) => {
      // Reset manual override on actual viewport change (resize)
      hasManualOverrideRef.current = false;
      applyCollapsedState(event.matches);
    };

    // Apply initial state ONLY on first mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      applyCollapsedState(mediaQuery.matches);
    }

    mediaQuery.addEventListener('change', handleViewportChange);
    return () => mediaQuery.removeEventListener('change', handleViewportChange);
  }, [isControlled, isCollapsedProp, onCollapseChange]);

  const ANIMATION_DURATION = 300;
  const isExpanded = !isCollapsed || isHovered;
  const isSliderActive = sliderType !== null || isSliderClosing;

  // Helper function to construct URL with locale prefix
  const getUrlWithLocale = useCallback(
    (path: string) => {
      if (selectedLanguage === 'en-us') {
        return path;
      }
      return `/${selectedLanguage}${path}`;
    },
    [selectedLanguage]
  );

  // Navigate using client-side router if available, otherwise full page reload.
  //
  // Cross-app routing logic (when ownRoutePrefixes is provided):
  //   1. Strip any locale prefix (e.g. /de-de) from the path before matching.
  //   2. Root path '/' is always handled client-side (dashboard link).
  //   3. If the stripped path starts with one of the ownRoutePrefixes → client-side navigate.
  //      Note: '/' entries in ownRoutePrefixes are skipped in the startsWith check because
  //      every path starts with '/' — root is handled by the exact-match guard in step 2.
  //   4. Otherwise → full page load via location.href (cross-app SPA navigation).
  const navigateTo = useCallback(
    (path: string) => {
      const url = getUrlWithLocale(path);

      if (ownRoutePrefixes && ownRoutePrefixes.length > 0) {
        // Strip locale prefix (e.g. /de-de/order-hub → /order-hub) before matching
        const basePath = path.replace(/^\/[a-z]{2}-[a-z]{2}/, '') || '/';
        const isOwnRoute =
          // Exact match for root — only treat '/' as own route when explicitly listed in
          // ownRoutePrefixes. SPAs that don't own the dashboard (e.g. services MFE) must
          // NOT have '/' in their prefixes, so clicking Dashboard triggers a full page load
          // back to partner portal. '/' must NOT be used in startsWith (it matches every path).
          (basePath === '/' && ownRoutePrefixes.includes('/')) ||
          ownRoutePrefixes.some((prefix) => prefix !== '/' && basePath.startsWith(prefix));

        if (!isOwnRoute) {
          // Path belongs to a different SPA — trigger a full page load
          globalThis.location.href = url;
          return;
        }
      }

      if (onNavigate) {
        onNavigate(url);
      } else {
        globalThis.location.href = url;
      }
    },
    [getUrlWithLocale, onNavigate, ownRoutePrefixes]
  );

  // Sync internal state when parent locale prop changes
  useEffect(() => {
    if (localeProp) {
      setSelectedLanguage(localeProp);
    }
  }, [localeProp]);

  // Transform roles data into dropdown options
  const roleOptions = rolesData.data.map((role) => ({
    value: role.id,
    label: role.name,
  }));

  // Get the selected role's scopes - memoized to prevent recreating on every render
  const allowedScopes = useMemo(() => {
    const selectedRoleData = rolesData.data.find((role) => role.id === selectedRole);
    return selectedRoleData?.scopes.map((scope) => scope.id) || [];
  }, [selectedRole]);

  // Filter workspaces based on selected role's scopes and apply localization
  // Memoized to prevent infinite re-renders in useEffect dependencies
  const filteredWorkspaces = useMemo(() => {
    const filtered = sidebarData.filter((workspace) => allowedScopes.includes(workspace.id));
    return filtered.map((workspace) => ({
      ...workspace,
      label: labels?.[`sidebar.${workspace.id}`] ?? workspace.label,
      childrens: workspace.childrens?.map((child) => ({
        ...child,
        label: labels?.[`sidebar.${workspace.id}.${child.id}`] ?? child.label,
      })),
    }));
  }, [allowedScopes, labels, sidebarData]);

  // Initialize selectedWorkspace based on filtered workspaces
  const [selectedWorkspace, setSelectedWorkspace] = useState(
    filteredWorkspaces[0] || sidebarData[0]
  );
  const [selectedChildItem, setSelectedChildItem] = useState<string | null>(null);

  // Keep selectedWorkspace labels in sync when filteredWorkspaces changes (e.g. language switch)
  useEffect(() => {
    const updated = filteredWorkspaces.find((w) => w.id === selectedWorkspace?.id);
    if (updated) {
      setSelectedWorkspace(updated);
    }
  }, [filteredWorkspaces]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally omit selectedWorkspace to avoid loop

  // Sync sidebar state with current URL on mount and path change
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let currentPath = currentPathProp || window.location.pathname;

    // Remove locale prefix if present for matching
    currentPath = currentPath.replace(/^\/[a-z]{2}-[a-z]{2}/, '');

    // Check if we're on the dashboard
    if (currentPath === '' || currentPath === '/') {
      setIsDashboardSelected(true);
      setSelectedChildItem(null);
      return;
    }

    // Find the matching workspace and child item
    for (const workspace of filteredWorkspaces) {
      if (workspace.childrens) {
        const matchingChild = workspace.childrens.find((child) =>
          currentPath.startsWith(child.path)
        );
        if (matchingChild) {
          setSelectedWorkspace(workspace);
          setSelectedChildItem(matchingChild.id);
          setIsDashboardSelected(false);
          return;
        }
      }
    }

    // No workspace matched — path is non-root but not in the current role's scopes.
    // Deselect Dashboard so it isn't incorrectly highlighted.
    setIsDashboardSelected(false);
    setSelectedChildItem(null);
  }, [filteredWorkspaces, currentPathProp]);

  const handleHamburgerClick = () => {
    const newCollapsedState = !isCollapsed;
    hasManualOverrideRef.current = true;
    if (!isControlled) {
      setInternalCollapsed(newCollapsedState);
    }
    setIsHovered(sliderType !== null);
    onCollapseChange?.(newCollapsedState);
  };

  const handleMouseEnter = () => {
    if (isCollapsed && !isSliderActive) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (isCollapsed && !isSliderActive) {
      setIsHovered(false);
    }
  };

  const handleWorkspaceToggle = () => {
    if (sliderType === 'workspace') {
      handleCloseSlider(false);
    } else {
      setSliderType('workspace');
      if (isCollapsed) {
        setIsHovered(true);
      }
    }
  };

  const handleCloseSlider = (closeSidebar = true) => {
    setIsSliderClosing(true);
    setTimeout(() => {
      setSliderType(null);
      setIsSliderClosing(false);
      if (isCollapsed && closeSidebar) {
        setIsHovered(false);
      }
    }, ANIMATION_DURATION);
  };

  const handleSettingsToggle = () => {
    if (sliderType === 'settings') {
      handleCloseSlider(false);
    } else {
      setSliderType('settings');
      if (isCollapsed) {
        setIsHovered(true);
      }
    }
  };

  const handleWorkspaceSelect = (workspaceId: string) => {
    const workspace = filteredWorkspaces.find((w) => w.id === workspaceId);
    if (!workspace) return;

    setSelectedWorkspace(workspace);
    setIsDashboardSelected(false);

    const firstChild = workspace.childrens?.[0];
    if (firstChild) {
      setSelectedChildItem(firstChild.id);
      navigateTo(firstChild.path);
    }

    handleCloseSlider(false);
    setTimeout(() => {
      setIsHovered(false);
    }, ANIMATION_DURATION);
  };

  const handleNavigationItemClick = (itemId: string, path: string) => {
    setIsDashboardSelected(false);
    setSelectedChildItem(itemId);
    navigateTo(path);
  };

  const handleDashboardClick = () => {
    setIsDashboardSelected(true);
    setSelectedChildItem(null);
    navigateTo('/');
  };

  const handleRoleSelect = (roleId: string) => {
    hasManualRoleSelectRef.current = true;
    setSelectedRole(roleId);
  };

  const handleLanguageSelect = (languageCode: string) => {
    setSelectedLanguage(languageCode);

    // Update localStorage
    localStorage.setItem('selectedLocale', languageCode);

    // Call parent callback - parent app handles navigation/routing
    onLocaleChange?.(languageCode);

    // Close the slider after selection — no language toggle step follows
    handleCloseSlider();
  };

  const handleSetLanguage = (languageCode: string | null) => {
    // Call parent callback
    onLanguageChange?.(languageCode);

    // Close the slider and collapse sidebar after selection
    handleCloseSlider();
  };

  // Update selected workspace when role changes
  useEffect(() => {
    if (filteredWorkspaces.length > 0) {
      const isCurrentWorkspaceAvailable = filteredWorkspaces.some(
        (w) => w.id === selectedWorkspace?.id
      );

      if (!isCurrentWorkspaceAvailable) {
        setSelectedWorkspace(filteredWorkspaces[0]);
        setSelectedChildItem(null);
        setIsDashboardSelected(true);
        navigateTo('/');
      }
    }
  }, [selectedRole, filteredWorkspaces, selectedWorkspace?.id, navigateTo]);

  return (
    <nav
      className={classnames(styles.sidebar, {
        [styles.collapsed]: isCollapsed,
        [styles.expanded]: !isCollapsed,
        [styles.collapsedState]: isCollapsed && !isHovered,
        [styles.isHovered]: isHovered,
      })}
      aria-label="Main navigation"
      data-theme={theme}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Header
        isExpanded={isExpanded}
        isCollapsed={isCollapsed}
        isHovered={isHovered}
        theme={theme}
        onHamburgerClick={handleHamburgerClick}
      />

      <Navigation
        isExpanded={isExpanded}
        theme={theme}
        selectedWorkspace={selectedWorkspace}
        selectedChildItem={selectedChildItem}
        onWorkspaceToggle={handleWorkspaceToggle}
        onChildItemClick={handleNavigationItemClick}
        labels={{ workspace: labels?.['sidebar.workspace'] }}
      />

      <Slider
        isOpen={sliderType !== null}
        isClosing={isSliderClosing}
        isCollapsed={isCollapsed}
        theme={theme}
        onClose={handleCloseSlider}
        variant={sliderType || 'workspace'}
        selectedWorkspace={selectedWorkspace?.id}
        onWorkspaceSelect={handleWorkspaceSelect}
        workspaces={filteredWorkspaces}
        onToggleTheme={onToggleTheme}
        labels={{
          workspace: labels?.['sidebar.workspace'],
          settings: labels?.['sidebar.settings'],
          appearance: labels?.['sidebar.appearance'],
          darkMode: labels?.['sidebar.darkMode'],
          lightMode: labels?.['sidebar.lightMode'],
        }}
        languageSelector={
          onLocaleChange ? (
            <LanguageSelector
              theme={theme}
              selectedLanguage={selectedLanguage}
              onLanguageSelect={handleLanguageSelect}
              localeOptions={localeOptions}
              labels={{
                region: labels?.region,
              }}
            />
          ) : undefined
        }
        languageToggle={
          onLanguageChange ? (
            <LanguageToggle
              theme={theme}
              locale={selectedLanguage}
              selectedLanguage={selectedLanguageProp}
              onLanguageChange={handleSetLanguage}
              labels={{
                label: labels?.language,
              }}
            />
          ) : undefined
        }
        rolesDropdown={
          showRoleDropdown ? (
            <Dropdown
              theme={theme}
              variant="list"
              size="medium"
              name="role-selector"
              options={roleOptions}
              selectedOption={selectedRole}
              label="Role"
              placeholder="Select Role"
              handleSelect={handleRoleSelect}
              gaComponent="sidebar-role-selector"
              logEvents={false}
            />
          ) : undefined
        }
      />

      <Footer
        isExpanded={isExpanded}
        isCollapsed={isCollapsed}
        isHovered={isHovered}
        theme={theme}
        userName={userName}
        userEmail={userEmail}
        onSignOut={onSignOut}
        onProfileClick={onProfileClick}
        signOutLabel={labels?.signOut}
        dashboardSection={
          <Box className={styles.dashboardSection}>
            <button
              className={classnames(styles.dashboardButton, {
                [styles.selected]: isDashboardSelected,
              })}
              onClick={handleDashboardClick}
            >
              <DashboardIcon size={20} color={theme === 'dark' ? 'white' : 'black'} />
              {isExpanded && (
                <Typography variant="body" size="small" color="white" theme={theme}>
                  {labels?.['sidebar.dashboard'] ?? 'Dashboard'}
                </Typography>
              )}
            </button>
            {knowledgeCenterLink && (
              <a
                href={knowledgeCenterLink.href}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.dashboardButton}
              >
                <KnowledgeCenterIcon size={20} color={theme === 'dark' ? 'white' : 'black'} />
                {isExpanded && (
                  <Typography variant="body" size="small" color="white" theme={theme}>
                    {knowledgeCenterLink.label}
                  </Typography>
                )}
              </a>
            )}
            <button
              className={classnames(styles.dashboardButton, {
                [styles.selected]: sliderType === 'settings',
              })}
              onClick={handleSettingsToggle}
              aria-label="Open settings"
            >
              <SettingsIcon size={20} color={theme === 'dark' ? 'white' : 'black'} />
              {isExpanded && (
                <Typography variant="body" size="small" color="white" theme={theme}>
                  {labels?.['sidebar.settings'] ?? 'Settings'}
                </Typography>
              )}
            </button>
          </Box>
        }
      />
    </nav>
  );
};

Sidebar.displayName = 'Sidebar';

export default Sidebar;
